import cloneDeep from 'lodash/cloneDeep';
import omit from 'lodash/omit';
import { v4 as uuidv4 } from 'uuid';
import { CONSTANTS, validHedmAttributeTypesByControlType } from './Configuration';
import isEqual from 'lodash/isEqual';
import { isApplicationFormTemplate } from '@nsr/applicant-apply-shared/src/helpers/applicationTemplateHelper';

const { controlTypes, hedmAttributeTypes } = CONSTANTS;

export const reorderAllControls = (allPages, allSections, allGroups, allQuestions) => {
    let newSections = [], newGroups = [], newQuestions = [];
    allPages.forEach(p => {
        const sectionsInPage = allSections.filter(s => s.pageId === p.id);
        sectionsInPage.forEach(s => {
            newSections.push(s);
            const groupsInSection = allGroups.filter(g => g.sectionId === s.id);
            groupsInSection.forEach(g => {
                newGroups.push(g);
                const questionsInGroup = allQuestions.filter(q => q.groupId === g.id);
                questionsInGroup.forEach(q => newQuestions.push(q));
            });
        });
    });
    return { newSections, newGroups, newQuestions };
};

export const destructureTemplate = (template) => {
    const { maxQuestionsPerRow, columnWidthOptions } = CONSTANTS;
    const defaultErrors = getDefaultErrors();
    let pages = [], sections = [], groups = [], questions = [];
    const messages = cloneDeep(template.templateMessages);
    const templateData = {
        ...cloneDeep(template),
        id: template.id,
    };
    delete templateData.pages;
    delete templateData.templateMessages;

    const emptyQuestion = {
        label: '',
        type: controlTypes.empty,
        errors: cloneDeep(defaultErrors),
    };

    template.pages && template.pages.forEach(p => {
        if (p.id) {
            const page = omit(p, ['sections']);
            page.errors = { ...defaultErrors, requiredError: !page.title };
            page.hasError = !page.title;
            pages.push(page);
            p.sections && p.sections.forEach(s => {
                if (s.id) {
                    const section = omit(s, ['groups']);
                    sections.push({ ...section, pageId: p.id, errors: cloneDeep(defaultErrors) });
                    s.groups && s.groups.forEach(g => {
                        if (g.id) {
                            const group = omit(g, ['questions']);
                            groups.push({ ...group, sectionId: s.id, pageId: p.id });
                            g.questions && g.questions.forEach(q => {
                                page.hasError = !q.label || page.hasError;
                                questions.push({
                                    ...cloneDeep(q), groupId: g.id, sectionId: s.id, pageId: p.id,
                                    errors: { ...defaultErrors, requiredError: !q.label }
                                });
                            });
                            // Add empty question if questions length < maxQuestionsPerRow
                            if (g.questions.length < maxQuestionsPerRow && g.questions[0]?.columnWidth != columnWidthOptions.extended) {
                                questions.push({ ...cloneDeep(emptyQuestion), id: uuidv4(), groupId: g.id, sectionId: s.id, pageId: p.id });
                            }
                        }
                    });
                    // Add empty group and question
                    const gId = uuidv4();
                    groups.push({ id: gId, sectionId: s.id, pageId: p.id });
                    questions.push({ ...cloneDeep(emptyQuestion), id: uuidv4(), groupId: gId, sectionId: s.id, pageId: p.id });
                }
            });
        }
    });
    return { templateData, pages, sections, groups, questions, messages };
};

export const restructureTemplate = (templateData, pages, sections, groups, questions, messages) => {
    if (!templateData || !pages || !sections || !groups) {
        return null;
    }
    const { controlTypes } = CONSTANTS;
    const template = cloneDeep(templateData);
    const tPages = cloneDeep(pages);
    const tSections = cloneDeep(sections);
    const tGroups = cloneDeep(groups);
    const tQuestions = cloneDeep(questions);
    template.templateMessages = cloneDeep(messages);
    tQuestions.forEach(q => {
        const qGroup = tGroups.find(g => g.id === q.groupId);
        if (qGroup && q.type !== controlTypes.empty) {
            const newQ = omit(q, ['groupId', 'sectionId', 'pageId', 'errors']);
            qGroup.questions ? qGroup.questions.push(newQ) : qGroup.questions = [newQ];
        }
    });
    tGroups.forEach(g => {
        const gSection = tSections.find(s => s.id === g.sectionId);
        if (gSection && g.questions) {
            const newG = omit(g, ['sectionId', 'pageId']);
            gSection.groups ? gSection.groups.push(newG) : gSection.groups = [newG];
        }
    });
    tSections.forEach(s => {
        const sPage = tPages.find(p => p.id === s.pageId);
        if (sPage) {
            const newS = omit(s, ['pageId', 'errors']);
            sPage.sections ? sPage.sections.push(newS) : sPage.sections = [newS];
        }
    });
    tPages.forEach(p => {
        delete p.errors;
        delete p.hasError;
    });
    template.pages = tPages;

    return template;
};

export const getSelectedControlIdFromAppTemplateControlType = (selectedControl) => {
    if (!selectedControl || !selectedControl?.controlType) {
        return null;
    }
    const controlType = selectedControl?.controlType;
    switch (controlType) {
        case CONSTANTS.appTemplateControlTypes.page:
            return selectedControl?.pageId ?? null;
        case CONSTANTS.appTemplateControlTypes.section:
            return selectedControl?.sectionId ?? null;
        case CONSTANTS.appTemplateControlTypes.group:
            return selectedControl?.groupId ?? null;
        case CONSTANTS.appTemplateControlTypes.question:
            return selectedControl?.questionId ?? null;
        default:
            return null;
    }
};

export const getDefaultErrors = () => {
    return { requiredError: false, conditionalError: false };
};

export const getEmptyQuestion = (pageId, sectionId, groupId) => ({
    labelId: '',
    type: CONSTANTS.controlTypes.empty,
    groupId,
    id: uuidv4(),
    sectionId,
    pageId,
    errors: cloneDeep(getDefaultErrors()),
});

export const didTemplateChange = (newTemplate, currentTemplate) => {
    return !newTemplate || newTemplate?.id !== currentTemplate.id;
};

export const reassignIds = (template) => {
    template.id = uuidv4();
    return template;
};

export const removeIdsForCreate = (template) => {
    delete template.id;
    if (template.templateMessages) {
        delete template.createdAt;
        delete template.updatedAt;
        delete template.templateMessages.id;
        delete template.templateMessages.templateId;
        delete template.templateMessages.createdAt;
        delete template.templateMessages.updatedAt;
    }
    return template;
};

export const reArrangeGroupsOnAdd = (applicationSections, applicationGroups, applicationQuestions, selectedControl) => {
    if (!applicationSections || !applicationGroups || !applicationQuestions || !selectedControl) {
        return { newApplicationGroups: [], newApplicationQuestions: [] };
    }
    const { maxQuestionsPerRow, controlTypes } = CONSTANTS;
    const s = applicationSections.find(x => x.id === selectedControl.sectionId);
    const g = applicationGroups.find(x => x.id === selectedControl.groupId);
    const gIndex = applicationGroups.findIndex(x => x.id === selectedControl.groupId);
    const qIndex = applicationQuestions.findIndex(x => x.id === selectedControl.questionId);
    const defaultErrors = getDefaultErrors();

    const emptyQuestion = {
        labelId: '',
        type: controlTypes.empty,
        groupId: g.id,
        sectionId: s.id,
        pageId: s.pageId,
        errors: cloneDeep(defaultErrors),
    };

    const emptyGroup = { sectionId: s.id, pageId: s.pageId };

    // If no. of questions in the group is less than maxQuestionsPerRow, add another empty question
    const qLength = applicationQuestions.filter(x => x.groupId === g.id).length;
    if (qLength < maxQuestionsPerRow) {
        applicationQuestions.splice((qIndex + 1), 0, { ...emptyQuestion, id: uuidv4() });
    }
    // If last group in section, and first question in that group, add another group with empty ques
    const lastGroup = applicationGroups.filter(x => x.sectionId === s.id).slice(-1)[0];
    if (lastGroup.id === g.id && qLength === 1) {
        const newGroupdId = uuidv4();
        applicationQuestions.splice((qIndex + 2), 0, { ...emptyQuestion, id: uuidv4(), groupId: newGroupdId });
        applicationGroups.splice((gIndex + 1), 0, { ...emptyGroup, id: newGroupdId });
    }
    const newApplicationGroups = applicationGroups;
    const newApplicationQuestions = applicationQuestions;

    return { newApplicationGroups, newApplicationQuestions };
};

export const getEthosPathDisplay = (path) => '/' + path.replaceAll('.', '/');

// Non-mapped attributes can be array attributes
export const isEthosPathArray = (path) => !path || /\[.*\]/.test(path);

export const isEthosAttributeValidForQuestion = (attribute, question) => {
    const validTypes = validHedmAttributeTypesByControlType[question.type];

    if (!validTypes || (
        // checkboxes/search and select fields must be mapped to simple arrays.
        !attribute.isSimpleArray && (
            question.type === controlTypes.checkbox ||
            question.type === controlTypes.searchandselect
        )
    )) {
        return false;
    }
    else if (attribute.type === hedmAttributeTypes.oneOf) {
        const oneOfTypes = attribute.oneOfTypes || [];

        return oneOfTypes.some(
            oneOfType => isEthosAttributeValidForQuestion(
                { ...attribute, ...oneOfType },
                question
            )
        );
    }
    else {
        return validTypes.includes(attribute.type);
    }
};

export const cloneExistingTemplate = (sourceTemplate, templateValues, templateType, applicationTemplateStatus, settingsApplicationTemplateType) => {
    var newTemplate = {
        ...(sourceTemplate),
        ...templateValues,
        externalLinkId: null,
        isDelivered: false,
        responsesCount: 0,
        status: CONSTANTS.applicationFormFilterMode.draft,
        lastSaved: new Date()
    };

    newTemplate.settings = {...(sourceTemplate?.settings), ...templateValues?.settings} || {};

    if (templateType) {
        newTemplate.templateType = templateType;
    }

    if (applicationTemplateStatus) {
        newTemplate.applicationTemplateStatus = applicationTemplateStatus;
    }

    if (settingsApplicationTemplateType) {
        newTemplate.settings.applicationTemplateType = settingsApplicationTemplateType;
    }

    // Copy internal description to external description if blank
    if (isApplicationFormTemplate(newTemplate.templateType)) {
        newTemplate.settings.externalDescription = newTemplate.description.substring(0, CONSTANTS.maxExternalDescriptionLength);
    }

    return newTemplate;
};

export const hasFormChanged = (originalApplicationTemplateData, applicationTemplateData, applicationPages, applicationSections, applicationGroups, applicationQuestions, applicationMessages) => {
    let changed = false;
    if (originalApplicationTemplateData?.templateData) {
        if (!isEqual(originalApplicationTemplateData?.templateData, applicationTemplateData)) {
            changed = true;
        }
        if (!isEqual(originalApplicationTemplateData?.pages, applicationPages)) {
            changed = true;
        }
        if (!isEqual(originalApplicationTemplateData?.sections, applicationSections)) {
            changed = true;
        }
        if (!isEqual(originalApplicationTemplateData?.groups, applicationGroups)) {
            changed = true;
        }
        if (!isEqual(originalApplicationTemplateData?.questions, applicationQuestions)) {
            changed = true;
        }
        if (!isEqual(originalApplicationTemplateData?.messages, applicationMessages)) {
            changed = true;
        }
    }
    return changed;
};